/**
 * ProjectsOverviewPage Component
 * AIDLC Administrative Backend
 *
 * Cross-project cockpit at /projects/overview.
 * Shows all projects with health indicators, agent allocation matrix,
 * and cross-project blockers.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectHealthCard } from './ProjectHealthCard';
import { AgentAllocationMatrix } from '../allocation/AgentAllocationMatrix';
import { CrossProjectDependencies } from '../blockers/CrossProjectDependencies';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { fetchProjectsOverview } from '../../../services/projectManagementService';
import type { ProjectOverview } from '../../../types/projects';

export function ProjectsOverviewPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchProjectsOverview();
        if (!cancelled) setProjects(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load projects overview');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <LoadingSpinner message="Loading projects overview..." />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Summary stats
  const totalProjects = projects.length;
  const greenCount = projects.filter((p) => p.health === 'green').length;
  const yellowCount = projects.filter((p) => p.health === 'yellow').length;
  const redCount = projects.filter((p) => p.health === 'red').length;
  const totalBlockers = projects.reduce((sum, p) => sum + p.blockerCount, 0);

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">All Projects Overview</h1>
          <p className="text-xs text-gray-500 mt-0.5">Cross-project health dashboard and resource allocation</p>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="text-xs text-ldaja-blue hover:underline"
        >
          Go to Project Management
        </button>
      </div>

      {/* Summary stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center dark:bg-zinc-900 dark:border-zinc-800">
          <div className="text-xl font-bold text-gray-800">{totalProjects}</div>
          <div className="text-[10px] text-gray-500">Total Projects</div>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-green-600">{greenCount}</div>
          <div className="text-[10px] text-gray-500">Healthy</div>
        </div>
        <div className="bg-white border border-yellow-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-yellow-600">{yellowCount}</div>
          <div className="text-[10px] text-gray-500">At Risk</div>
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-red-600">{redCount}</div>
          <div className="text-[10px] text-gray-500">Critical</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center dark:bg-zinc-900 dark:border-zinc-800">
          <div className={`text-xl font-bold ${totalBlockers > 0 ? 'text-red-600' : 'text-gray-800'}`}>
            {totalBlockers}
          </div>
          <div className="text-[10px] text-gray-500">Open Blockers</div>
        </div>
      </div>

      {/* Project health cards */}
      {projects.length === 0 ? (
        <EmptyState
          title="No projects"
          description="No projects have been created yet."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {projects.map((project) => (
            <ProjectHealthCard
              key={project.id}
              project={project}
              onClick={() => navigate('/projects')}
            />
          ))}
        </div>
      )}

      {/* Agent Allocation Matrix */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">
          Agent Allocation Matrix
        </h2>
        <AgentAllocationMatrix />
      </div>

      {/* Cross-Project Blockers */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">
          Cross-Project Blockers
        </h2>
        <CrossProjectDependencies />
      </div>
    </div>
  );
}
